/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import { useExamStore } from '@/store/ExamStore/ExamStore';
import { useGetExercisesWithDisciplinesAndSeries } from '@/api/hooks/useExercisesWithDisciplinesAndSeries';
import {
  ExerciseOutput,
  QuestionType,
  QuestionOutput,
} from '@/api/gql/graphql';
import { useCreateQuizz, useUpdateQuizz } from '@/api/hooks/useQuizz';
import { useToast } from '@/hooks/useToast';
import { useQuestionLogic } from '@/hooks/useQuestionLogic';
import { QuizzFormValues, useHandleQuizzForm } from '@/hooks/useQuizzForm';
import { createMenuItem } from '@/components/Common/DropdownMenu/MenuDropdown.utils';
import { useQuizStore } from '@/store/QuizStore/QuizStore';
import { useEvaluationStore } from '@/store/EvaluationStore/EvaluationStore';
import { useRemoveQuestion, useDeleteExercise } from '@/api/hooks/useExercise';
import axios from 'axios';
import { createFile } from '@/api/fileUpload';
import { useFileUploadStore } from '@/store/FileUploadStore/FileUploadStore';

export type ExerciseTabItem = {
  id: number;
  title: string;
  type: 'evaluation' | 'exercice';
};

export const useSettingUpTests = () => {
  // Store hooks
  const {
    setIsOpenExamModal,
    activeTabExercise,
    setActiveTabExercise,
    setEditingTab,
    isOpenExerciseDeleteModal,
    setIsOpenExerciseDeleteModal,
    exerciseToDelete,
    setExerciseToDelete,
  } = useExamStore();

  const {
    openQuizModal,
    initialSelectedOptions,
    isOpenQuizDeleteModal,
    openQuizDeleteModal,
    selectedQuizz,
    setSelectedQuizz,
  } = useQuizStore();
  const { setSelectedFileName } = useFileUploadStore();

  const {
    isOpenEvaluationDeleteModal: isOpenQuestionDeleteModal,
    setIsOpenEvaluationDeleteModal: setIsOpenQuestionDeleteModal,
  } = useEvaluationStore();

  const { showToast } = useToast();
  const [selectedExercise, setSelectedExercise] =
    useState<ExerciseOutput | null>(null);
  const [isEditingQuestion, setIsEditingQuestion] = useState<boolean>(false);
  const [exerciseTabs, setExerciseTabs] = useState<ExerciseTabItem[]>([]);

  useEffect(() => {
    setSelectedQuizz(null);
  }, []);

  // API hooks
  const { data: exercisesWithDisciplinesAndSeries } =
    useGetExercisesWithDisciplinesAndSeries();

  const { mutate: updateQuestion, isPending: loadingUpdateQuestion } =
    useUpdateQuizz();
  const { mutate: onDeleteQuestion, isPending: isDeleteQuestionPending } =
    useRemoveQuestion();
  const { mutate: createQuestion, isPending: isCreateQuestionPending } =
    useCreateQuizz();
  const { mutate: deleteExercise, isPending: isDeleteExercisePending } =
    useDeleteExercise();

  // Question logic
  const {
    selectedType,
    setSelectedType,
    response1Check,
    setResponse1Check,
    response2Check,
    setResponse2Check,
    response3Check,
    setResponse3Check,
    response4Check,
    setResponse4Check,
    formatResponses,
    resetResponses,
  } = useQuestionLogic();

  const { register, handleSubmit, setValue, errors, reset } =
    useHandleQuizzForm('create');

  const QuizzQuestionType = [
    { _id: QuestionType.Qcm, name: QuestionType.Qcm },
    { _id: QuestionType.TrueOrFalse, name: QuestionType.TrueOrFalse },
    { _id: QuestionType.MissingWords, name: QuestionType.MissingWords },
  ];

  // Effects
  useEffect(() => {
    if (exercisesWithDisciplinesAndSeries) {
      setExerciseTabs(
        exercisesWithDisciplinesAndSeries.map((exercise) => ({
          id: exercise._id,
          title: exercise.name,
          type: 'evaluation',
        })),
      );
    }
  }, [exercisesWithDisciplinesAndSeries]);

  // Récupérer les questions de l'exercice actif
  const getActiveExerciseQuestions = () => {
    const activeExercise = exercisesWithDisciplinesAndSeries?.find(
      (exercise) => exercise._id === exerciseTabs[activeTabExercise]?.id,
    );
    return activeExercise?.questions || [];
  };

  const handleEditTab = (id: number) => {
    const selectedExercise = exercisesWithDisciplinesAndSeries?.find(
      (exercise) => exercise._id === id,
    );
    if (selectedExercise) {
      setSelectedExercise(selectedExercise);
      setIsOpenExamModal(true);
      setEditingTab(true);
    }
  };

  const handleEditQuestion = (question: QuestionOutput) => {
    if (!question) return;

    setSelectedQuizz(question);
    setValue('name', question.name ?? '');
    setValue('description', question.description || '');
    setValue('type', question.type);
    setSelectedType(question.type);
    setSelectedFileName(question.image || '');

    if (question.responses) {
      question.responses.forEach((response, index) => {
        const responseNumber = index + 1;
        setValue(`reponse${responseNumber}` as any, response.response || '');
        if (responseNumber === 1) setResponse1Check(response.isRight);
        if (responseNumber === 2) setResponse2Check(response.isRight);
        if (responseNumber === 3) setResponse3Check(response.isRight);
        if (responseNumber === 4) setResponse4Check(response.isRight);
      });
    }
  };

  const handleCreateQuestion = async (values: QuizzFormValues) => {
    try {
      const responses = formatResponses(values);
      const hasAtLeastOneTrue = responses.some(
        (response: { isRight: boolean }) => response.isRight,
      );

      if (selectedType) {
        const type = Object.values(selectedType)[0];
        if (!hasAtLeastOneTrue) {
          const warningMessage =
            type === QuestionType.Qcm || type === QuestionType.MissingWords
              ? `Pour un ${type}, il doit y avoir au moins une réponse correcte.`
              : 'Pour TRUE_OR_FALSE, il doit y avoir au moins une réponse correcte.';
          showToast(warningMessage, 'warning', {
            autoClose: 3000,
            position: 'top-right',
          });
          return;
        }
      }

      const activeExercise = exerciseTabs[activeTabExercise];
      if (!activeExercise) {
        showToast('Veuillez sélectionner un exercice', 'error', {
          autoClose: 3000,
          position: 'top-right',
        });
        return;
      }

      let imageUrl = null;
      if (values.picture && values.picture instanceof File) {
        try {
          const token = localStorage.getItem('authToken') ?? '';
          const response = await axios(createFile(values.picture, token));
          if (response.status !== 201) {
            throw new Error("Erreur lors de l'ajout de l'image");
          }
          imageUrl = response.data?.publicUrl || response.data;
        } catch (error) {
          showToast("Erreur lors de l'upload de l'image", 'error', {
            autoClose: 3000,
            position: 'top-right',
          });
          return;
        }
      }

      const questionData = {
        name: values.name ?? '',
        type: values?.type as QuestionType,
        responses,
        description: values.description,
        exercise: activeExercise.id,
        ...(imageUrl && { image: imageUrl }),
      };

      createQuestion(questionData, {
        onSuccess: () => {
          showToast('Question ajoutée', 'success', {
            autoClose: 3000,
            position: 'top-right',
          });
          reset();
          resetResponses();
          setSelectedType(null);
          setValue('name', '');
          setValue('description', '');
          setValue('type', undefined);
          setValue('reponse1', '');
          setValue('reponse2', '');
          setValue('reponse3', '');
          setValue('reponse4', '');
          setValue('picture', undefined);
          setResponse1Check(false);
          setResponse2Check(false);
          setResponse3Check(false);
          setResponse4Check(false);
          setSelectedFileName('');
        },
        onError: (error: { message: string }) => {
          showToast(error?.message, 'error', {
            autoClose: 3000,
            position: 'top-right',
          });
        },
      });
    } catch (error) {
      console.error('Error:', error);
      showToast(
        "Une erreur s'est produite lors de la création de la question",
        'error',
        {
          autoClose: 3000,
          position: 'top-right',
        },
      );
    }
  };

  const handleUpdateQuestion = async (values: QuizzFormValues) => {
    try {
      const responses = formatResponses(values);
      const hasAtLeastOneTrue = responses.some(
        (response: { isRight: boolean }) => response.isRight,
      );

      if (selectedType) {
        const type = selectedType;
        if (!hasAtLeastOneTrue) {
          const warningMessage =
            type === QuestionType.Qcm || type === QuestionType.MissingWords
              ? `Pour un ${type}, il doit y avoir au moins une réponse correcte.`
              : 'Pour TRUE_OR_FALSE, il doit y avoir au moins une réponse correcte.';
          showToast(warningMessage, 'warning', {
            autoClose: 3000,
            position: 'top-right',
          });
          return;
        }
      }

      const activeExercise = exerciseTabs[activeTabExercise];
      if (!activeExercise) {
        showToast('Veuillez sélectionner un exercice', 'error', {
          autoClose: 3000,
          position: 'top-right',
        });
        return;
      }

      let imageUrl = null;
      if (values.picture && values.picture instanceof File) {
        try {
          const token = localStorage.getItem('authToken') ?? '';
          const response = await axios(createFile(values.picture, token));
          if (response.status !== 201) {
            throw new Error("Erreur lors de l'ajout de l'image");
          }
          imageUrl = response.data?.publicUrl || response.data;
        } catch (error) {
          showToast("Erreur lors de l'upload de l'image", 'error', {
            autoClose: 3000,
            position: 'top-right',
          });
          return;
        }
      }

      updateQuestion(
        {
          updateQuestionId: selectedQuizz?._id,
          input: {
            name: values.name ?? '',
            type: values?.type as QuestionType,
            responses,
            description: values.description,
            ...(imageUrl && { image: imageUrl }),
          },
        },
        {
          onSuccess: () => {
            showToast('Question modifiée', 'success', {
              autoClose: 3000,
              position: 'top-right',
            });
            setIsEditingQuestion(false);
            reset();
            resetResponses();
            setSelectedType(null);
            setValue('name', '');
            setValue('description', '');
            setValue('type', undefined);
            setValue('reponse1', '');
            setValue('reponse2', '');
            setValue('reponse3', '');
            setValue('reponse4', '');
            setResponse1Check(false);
            setResponse2Check(false);
            setResponse3Check(false);
            setResponse4Check(false);
            setSelectedQuizz(null);
            setSelectedFileName('');
          },
          onError: (error: { message: string }) => {
            showToast(error?.message, 'error', {
              autoClose: 3000,
              position: 'top-right',
            });
          },
        },
      );
    } catch (error) {
      console.error('Error:', error);
      showToast(
        "Une erreur s'est produite lors de la modification de la question",
        'error',
        {
          autoClose: 3000,
          position: 'top-right',
        },
      );
    }
  };

  const handleDeleteQuestion = () => {
    onDeleteQuestion(selectedQuizz?._id, {
      onSuccess: () => {
        showToast('Question supprimée', 'success', {
          autoClose: 3000,
          position: 'top-right',
        });
        setIsOpenQuestionDeleteModal(false);
      },
      onError: (error: { message: string }) => {
        showToast(error?.message, 'error', {
          autoClose: 3000,
          position: 'top-right',
        });
      },
    });
  };

  const handleDeleteExercise = () => {
    if (!exerciseToDelete) return;

    deleteExercise(exerciseToDelete.toString(), {
      onSuccess: () => {
        showToast('Exercice supprimé', 'success', {
          autoClose: 3000,
          position: 'top-right',
        });
        setIsOpenExerciseDeleteModal(false);
        setExerciseToDelete(null);
      },
      onError: (error: { message: string }) => {
        showToast(error?.message, 'error', {
          autoClose: 3000,
          position: 'top-right',
        });
      },
    });
  };

  const handleOpenDeleteModal = (exerciseId: number) => {
    setExerciseToDelete(exerciseId);
    setIsOpenExerciseDeleteModal(true);
  };

  const getQuestionMenuItems = (question: QuestionOutput) => [
    createMenuItem('Modifier', 'font-[400]', () => {
      setIsEditingQuestion(true);
      handleEditQuestion(question);
    }),
    createMenuItem('Supprimer', 'font-[400] text-[#EA242C]', () => {
      setSelectedQuizz(question);
      setIsOpenQuestionDeleteModal(true);
    }),
  ];

  return {
    exerciseTabs,
    selectedExercise,
    handleEditTab,
    activeTabExercise,
    setActiveTabExercise,
    setIsOpenExamModal,
    QuizzQuestionType,
    register,
    handleSubmit,
    setValue,
    errors,
    questions: getActiveExerciseQuestions(),
    openQuizModal,
    handleSelectedQuizz: (question: QuestionOutput) => {
      setSelectedQuizz(question);
    },
    setSelectedQuizz,
    selectedQuizz,
    quizzMenuItems: selectedQuizz ? getQuestionMenuItems(selectedQuizz) : [],
    handleCreateQuestion,
    handleSelectedType: setSelectedType,
    selectedType,
    response1Check,
    setResponse1Check,
    response2Check,
    setResponse2Check,
    response3Check,
    setResponse3Check,
    response4Check,
    setResponse4Check,
    initialSelectedOptions,
    isOpenQuizDeleteModal,
    openQuizDeleteModal,
    isDeleteQuestionPending,
    handleDeleteQuestion,
    handleUpdateQuestion,
    handleEditQuestion,
    isEditingQuestion,
    isOpenQuestionDeleteModal,
    setIsOpenQuestionDeleteModal,
    isOpenExerciseDeleteModal,
    setIsOpenExerciseDeleteModal,
    isDeleteExercisePending,
    handleDeleteExercise,
    handleOpenDeleteModal,
    isCreateQuestionPending,
    loadingUpdateQuestion,
  };
};
